import React, { useState } from "react";
import { api, formatBRL, formatDateBR } from "../lib/api";
import { toast } from "sonner";
import { X, Loader2, Percent, Calendar, DollarSign, AlertTriangle } from "lucide-react";

// ---------- Shared preview table ----------
function PreviewTable({ preview }) {
  if (!preview) return null;
  return (
    <div className="rounded-md border border-[#D4AF37]/30 bg-[#0f0f0f] p-3 mt-3" data-testid="edit-preview">
      <div className="text-[10px] uppercase tracking-wider text-[#D4AF37] mb-2">Prévia — parcelas afetadas</div>
      <div className="overflow-x-auto max-h-72">
        <table className="w-full text-[11.5px]">
          <thead className="text-[10px] uppercase text-[#737373]">
            <tr>
              <th className="text-left py-1">#</th>
              <th className="text-left py-1">Vencimento</th>
              <th className="text-right py-1">Antes</th>
              <th className="text-right py-1">Depois</th>
              <th className="text-left py-1 pl-3">Status</th>
            </tr>
          </thead>
          <tbody>
            {preview.installments.map((row) => {
              const changed = JSON.stringify(row.before) !== JSON.stringify(row.after);
              return (
                <tr key={row.number} className={`border-t border-[#141414] ${row.locked ? "opacity-60" : ""}`} data-testid={`preview-row-${row.number}`}>
                  <td className="py-1 font-mono">{row.number}</td>
                  <td className="py-1">{formatDateBR(row.after?.due_date || row.before?.due_date)}</td>
                  <td className="py-1 text-right font-mono text-[#A3A3A3]">{formatBRL(row.before?.amount)}</td>
                  <td className={`py-1 text-right font-mono ${changed ? "text-[#D4AF37]" : "text-[#737373]"}`}>{formatBRL(row.after?.amount)}</td>
                  <td className="py-1 pl-3">
                    {row.locked ? (
                      <span className="text-emerald-300 text-[10px]">TRAVADA (paga)</span>
                    ) : changed ? (
                      <span className="text-[#D4AF37] text-[10px]">Alterada</span>
                    ) : (
                      <span className="text-[#737373] text-[10px]">Mantida</span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-3 text-[11px]">
        <SummaryCell label="Total (antes)" value={formatBRL(preview.before?.total_amount)} />
        <SummaryCell label="Total (depois)" value={formatBRL(preview.after?.total_amount)} accent />
        <SummaryCell label="Saldo (antes)" value={formatBRL(preview.before?.balance_remaining)} />
        <SummaryCell label="Saldo (depois)" value={formatBRL(preview.after?.balance_remaining)} accent />
      </div>
    </div>
  );
}

function SummaryCell({ label, value, accent }) {
  return (
    <div className="rounded border border-[#1f1f1f] bg-[#0a0a0a] px-2 py-1.5">
      <div className="text-[9.5px] uppercase tracking-wider text-[#737373]">{label}</div>
      <div className={`font-mono text-[11.5px] mt-0.5 ${accent ? "text-[#D4AF37]" : ""}`}>{value}</div>
    </div>
  );
}

function ModalShell({ title, icon: Icon, subtitle, onClose, children, testId }) {
  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-start justify-center p-4 overflow-y-auto" data-testid={testId}>
      <div className="fc-card w-full max-w-2xl my-8 p-6">
        <div className="flex items-start justify-between mb-4">
          <div>
            <h3 className="font-display text-lg font-semibold flex items-center gap-2">
              <Icon size={16} className="text-[#D4AF37]" /> {title}
            </h3>
            {subtitle && <p className="text-[12px] text-[#A3A3A3] mt-1">{subtitle}</p>}
          </div>
          <button onClick={onClose} className="text-[#737373] hover:text-white" data-testid={`${testId}-close`}>
            <X size={16} />
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

function ReasonInput({ reason, setReason, testId }) {
  return (
    <div>
      <label className="text-[11px] uppercase tracking-wider text-[#737373] block mb-1">
        Motivo (obrigatório, mín. 5 caracteres)
      </label>
      <textarea
        className="fc-input w-full text-[13px]"
        rows={2}
        required
        minLength={5}
        value={reason}
        onChange={(e) => setReason(e.target.value)}
        placeholder="Ex.: ajuste operacional aprovado pela gestora"
        data-testid={testId}
      />
    </div>
  );
}

// =====================================================================
// 1) INTEREST
// =====================================================================
export function EditInstallmentInterestModal({ contract, onClose, onDone }) {
  const [newPct, setNewPct] = useState(String(contract.interest_percent ?? ""));
  const [reason, setReason] = useState("");
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const doPreview = async () => {
    const pct = parseFloat(String(newPct).replace(",", "."));
    if (!Number.isFinite(pct) || pct < 0) { toast.error("% inválido"); return; }
    setLoading(true);
    try {
      const { data } = await api.post(`/installment-contracts/${contract.id}/change-interest`, {
        new_interest_percent: pct, reason: reason || "prévia", dry_run: true,
      });
      setPreview(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha na prévia");
    } finally { setLoading(false); }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (reason.trim().length < 5) { toast.error("Motivo mín. 5 caracteres"); return; }
    if (!preview) { toast.error("Gere a prévia antes"); return; }
    setSaving(true);
    try {
      await api.post(`/installment-contracts/${contract.id}/change-interest`, {
        new_interest_percent: preview.after.interest_percent,
        reason: reason.trim(),
        dry_run: false,
      });
      toast.success("% de juros atualizado");
      onDone();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha ao alterar");
    } finally { setSaving(false); }
  };

  return (
    <ModalShell
      title="Alterar % de juros"
      icon={Percent}
      subtitle="Recalcula apenas as parcelas pendentes. Pagamentos e comissões efetivadas ficam intocados."
      onClose={onClose}
      testId="edit-installment-interest-modal"
    >
      <form onSubmit={submit} className="space-y-3">
        <div className="grid grid-cols-3 gap-3">
          <div className="rounded-md border border-[#1f1f1f] bg-[#0a0a0a] px-3 py-2">
            <div className="text-[10px] uppercase tracking-wider text-[#737373]">Capital</div>
            <div className="font-mono text-sm mt-0.5">{formatBRL(contract.capital)}</div>
          </div>
          <div className="rounded-md border border-[#1f1f1f] bg-[#0a0a0a] px-3 py-2">
            <div className="text-[10px] uppercase tracking-wider text-[#737373]">% atual</div>
            <div className="font-mono text-sm mt-0.5 text-[#A3A3A3]" data-testid="edit-interest-current">{contract.interest_percent ?? 0}%</div>
          </div>
          <div>
            <label className="text-[11px] uppercase tracking-wider text-[#737373] block mb-1">Novo %</label>
            <input
              type="number" step="0.01" min="0" max="1000" required
              className="fc-input w-full text-[13px]"
              value={newPct}
              onChange={(e) => { setNewPct(e.target.value); setPreview(null); }}
              data-testid="edit-interest-new-pct"
            />
          </div>
        </div>

        <button type="button" onClick={doPreview} disabled={loading} className="fc-btn-ghost text-xs" data-testid="edit-interest-preview-btn">
          {loading ? <Loader2 size={12} className="animate-spin" /> : null} Gerar prévia
        </button>

        <PreviewTable preview={preview} />

        <ReasonInput reason={reason} setReason={setReason} testId="edit-interest-reason" />

        <div className="flex justify-end gap-2 pt-2">
          <button type="button" onClick={onClose} className="fc-btn-ghost text-xs">Cancelar</button>
          <button type="submit" disabled={saving || !preview} className="fc-btn-gold text-xs flex items-center gap-1.5" data-testid="edit-interest-submit">
            {saving ? <Loader2 size={12} className="animate-spin" /> : <Percent size={12} />} Confirmar
          </button>
        </div>
      </form>
    </ModalShell>
  );
}

// =====================================================================
// 2) DUE DATES
// =====================================================================
export function EditInstallmentDueDatesModal({ contract, onClose, onDone }) {
  const [mode, setMode] = useState("base_day"); // 'base_day' | 'single'
  const [baseDay, setBaseDay] = useState("");
  const [instNumber, setInstNumber] = useState("");
  const [newDate, setNewDate] = useState("");
  const [reason, setReason] = useState("");
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const pending = (contract.installments || []).filter(i => i.status === "pendente" || i.status === "atrasada");

  const buildPayload = () => {
    if (mode === "base_day") {
      const bd = parseInt(baseDay, 10);
      if (!bd || bd < 1 || bd > 31) return null;
      return { mode: "base_day", new_base_day: bd };
    }
    const n = parseInt(instNumber, 10);
    if (!n || !newDate) return null;
    return { mode: "single", installment_number: n, new_due_date: newDate };
  };

  const doPreview = async () => {
    const p = buildPayload();
    if (!p) { toast.error("Preencha os campos"); return; }
    setLoading(true);
    try {
      const { data } = await api.post(`/installment-contracts/${contract.id}/change-due-dates`, {
        ...p, reason: reason || "prévia", dry_run: true,
      });
      setPreview(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha na prévia");
    } finally { setLoading(false); }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (reason.trim().length < 5) { toast.error("Motivo mín. 5 caracteres"); return; }
    const p = buildPayload();
    if (!p) { toast.error("Preencha os campos"); return; }
    setSaving(true);
    try {
      await api.post(`/installment-contracts/${contract.id}/change-due-dates`, {
        ...p, reason: reason.trim(), dry_run: false,
      });
      toast.success("Vencimentos atualizados");
      onDone();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha ao alterar");
    } finally { setSaving(false); }
  };

  const isMonthly = contract.frequency === "monthly";

  return (
    <ModalShell
      title="Alterar vencimento"
      icon={Calendar}
      subtitle="Escolha um dia-base para todas as parcelas pendentes ou altere uma parcela específica."
      onClose={onClose}
      testId="edit-installment-duedates-modal"
    >
      <form onSubmit={submit} className="space-y-3">
        <div className="flex gap-2" data-testid="edit-duedates-mode">
          {isMonthly && (
            <button type="button" onClick={() => { setMode("base_day"); setPreview(null); }} data-testid="mode-base-day"
              className={`px-3 py-1.5 rounded text-[12px] border ${mode === "base_day" ? "bg-[#D4AF37] text-black border-[#D4AF37]" : "border-[#262626] text-[#A3A3A3]"}`}>
              Dia-base (todas)
            </button>
          )}
          <button type="button" onClick={() => { setMode("single"); setPreview(null); }} data-testid="mode-single"
            className={`px-3 py-1.5 rounded text-[12px] border ${mode === "single" ? "bg-[#D4AF37] text-black border-[#D4AF37]" : "border-[#262626] text-[#A3A3A3]"}`}>
            Parcela específica
          </button>
        </div>

        {mode === "base_day" ? (
          <div>
            <label className="text-[11px] uppercase tracking-wider text-[#737373] block mb-1">Novo dia-base (1-31)</label>
            <input type="number" min="1" max="31" required className="fc-input w-full text-[13px]"
              value={baseDay} onChange={(e) => { setBaseDay(e.target.value); setPreview(null); }}
              data-testid="edit-duedates-base-day" />
            <p className="text-[10.5px] text-[#737373] mt-1">Ex.: dia 31 → mantém 31 em meses de 31 dias; usa último dia nos demais.</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[11px] uppercase tracking-wider text-[#737373] block mb-1">Parcela #</label>
              <select className="fc-input w-full text-[13px]" required value={instNumber}
                onChange={(e) => { setInstNumber(e.target.value); setPreview(null); }}
                data-testid="edit-duedates-inst-number">
                <option value="">Escolher…</option>
                {pending.map(p => (<option key={p.id} value={p.number}>#{p.number} — {formatDateBR(p.due_date)}</option>))}
              </select>
            </div>
            <div>
              <label className="text-[11px] uppercase tracking-wider text-[#737373] block mb-1">Nova data</label>
              <input type="date" required className="fc-input w-full text-[13px]"
                value={newDate} onChange={(e) => { setNewDate(e.target.value); setPreview(null); }}
                data-testid="edit-duedates-new-date" />
            </div>
          </div>
        )}

        <button type="button" onClick={doPreview} disabled={loading} className="fc-btn-ghost text-xs" data-testid="edit-duedates-preview-btn">
          {loading ? <Loader2 size={12} className="animate-spin" /> : null} Gerar prévia
        </button>

        <PreviewTable preview={preview} />
        <ReasonInput reason={reason} setReason={setReason} testId="edit-duedates-reason" />

        <div className="flex justify-end gap-2 pt-2">
          <button type="button" onClick={onClose} className="fc-btn-ghost text-xs">Cancelar</button>
          <button type="submit" disabled={saving || !preview} className="fc-btn-gold text-xs flex items-center gap-1.5" data-testid="edit-duedates-submit">
            {saving ? <Loader2 size={12} className="animate-spin" /> : <Calendar size={12} />} Confirmar
          </button>
        </div>
      </form>
    </ModalShell>
  );
}

// =====================================================================
// 3) VALUES
// =====================================================================
export function EditInstallmentValuesModal({ contract, onClose, onDone }) {
  const [mode, setMode] = useState("total"); // 'total' | 'count' | 'single'
  const [newTotal, setNewTotal] = useState(String(contract.total_amount ?? ""));
  const [newCount, setNewCount] = useState("");
  const [instNumber, setInstNumber] = useState("");
  const [newAmount, setNewAmount] = useState("");
  const [reason, setReason] = useState("");
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const pending = (contract.installments || []).filter(i => i.status === "pendente" || i.status === "atrasada");

  const buildPayload = () => {
    if (mode === "total") {
      const v = parseFloat(String(newTotal).replace(",", "."));
      if (!Number.isFinite(v) || v <= 0) return null;
      return { mode: "total", new_total_amount: v };
    }
    if (mode === "count") {
      const n = parseInt(newCount, 10);
      if (!n || n < 1) return null;
      return { mode: "count", new_pending_count: n };
    }
    const n = parseInt(instNumber, 10);
    const v = parseFloat(String(newAmount).replace(",", "."));
    if (!n || !Number.isFinite(v)) return null;
    return { mode: "single", installment_number: n, new_amount: v };
  };

  const doPreview = async () => {
    const p = buildPayload();
    if (!p) { toast.error("Preencha os campos"); return; }
    setLoading(true);
    try {
      const { data } = await api.post(`/installment-contracts/${contract.id}/change-values`, {
        ...p, reason: reason || "prévia", dry_run: true,
      });
      setPreview(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha na prévia");
    } finally { setLoading(false); }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (reason.trim().length < 5) { toast.error("Motivo mín. 5 caracteres"); return; }
    const p = buildPayload();
    if (!p) { toast.error("Preencha os campos"); return; }
    setSaving(true);
    try {
      await api.post(`/installment-contracts/${contract.id}/change-values`, {
        ...p, reason: reason.trim(), dry_run: false,
      });
      toast.success("Valores atualizados");
      onDone();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha ao alterar");
    } finally { setSaving(false); }
  };

  return (
    <ModalShell
      title="Alterar valores"
      icon={DollarSign}
      subtitle="Ajuste total, quantidade de parcelas pendentes ou valor de uma parcela específica. Nunca reduz abaixo do já pago."
      onClose={onClose}
      testId="edit-installment-values-modal"
    >
      <form onSubmit={submit} className="space-y-3">
        <div className="flex flex-wrap gap-2" data-testid="edit-values-mode">
          {[
            { v: "total", label: "Valor total" },
            { v: "count", label: "Qtd. parcelas pendentes" },
            { v: "single", label: "Parcela específica" },
          ].map(o => (
            <button key={o.v} type="button" onClick={() => { setMode(o.v); setPreview(null); }} data-testid={`values-mode-${o.v}`}
              className={`px-3 py-1.5 rounded text-[12px] border ${mode === o.v ? "bg-[#D4AF37] text-black border-[#D4AF37]" : "border-[#262626] text-[#A3A3A3]"}`}>
              {o.label}
            </button>
          ))}
        </div>

        {mode === "total" && (
          <div className="rounded border border-amber-500/30 bg-amber-500/5 p-3 text-[11px] text-amber-200 flex items-start gap-2">
            <AlertTriangle size={13} className="mt-0.5" />
            <span>Capital lent permanece. Diferença absorvida pelo lucro. Não pode ser menor que o total já recebido.</span>
          </div>
        )}

        {mode === "total" && (
          <div>
            <label className="text-[11px] uppercase tracking-wider text-[#737373] block mb-1">Novo total</label>
            <input type="number" step="0.01" min="0" required className="fc-input w-full text-[13px]"
              value={newTotal} onChange={(e) => { setNewTotal(e.target.value); setPreview(null); }}
              data-testid="edit-values-new-total" />
          </div>
        )}
        {mode === "count" && (
          <div>
            <label className="text-[11px] uppercase tracking-wider text-[#737373] block mb-1">Nova quantidade de parcelas pendentes</label>
            <input type="number" min="1" required className="fc-input w-full text-[13px]"
              value={newCount} onChange={(e) => { setNewCount(e.target.value); setPreview(null); }}
              data-testid="edit-values-new-count" />
            <p className="text-[10.5px] text-[#737373] mt-1">Reaproveita datas existentes; cria/cancela quando muda a quantidade.</p>
          </div>
        )}
        {mode === "single" && (
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[11px] uppercase tracking-wider text-[#737373] block mb-1">Parcela #</label>
              <select className="fc-input w-full text-[13px]" required value={instNumber}
                onChange={(e) => { setInstNumber(e.target.value); setPreview(null); }}
                data-testid="edit-values-inst-number">
                <option value="">Escolher…</option>
                {pending.map(p => (<option key={p.id} value={p.number}>#{p.number} — {formatBRL(p.amount)}</option>))}
              </select>
            </div>
            <div>
              <label className="text-[11px] uppercase tracking-wider text-[#737373] block mb-1">Novo valor</label>
              <input type="number" step="0.01" min="0" required className="fc-input w-full text-[13px]"
                value={newAmount} onChange={(e) => { setNewAmount(e.target.value); setPreview(null); }}
                data-testid="edit-values-new-amount" />
            </div>
          </div>
        )}

        <button type="button" onClick={doPreview} disabled={loading} className="fc-btn-ghost text-xs" data-testid="edit-values-preview-btn">
          {loading ? <Loader2 size={12} className="animate-spin" /> : null} Gerar prévia
        </button>

        <PreviewTable preview={preview} />
        <ReasonInput reason={reason} setReason={setReason} testId="edit-values-reason" />

        <div className="flex justify-end gap-2 pt-2">
          <button type="button" onClick={onClose} className="fc-btn-ghost text-xs">Cancelar</button>
          <button type="submit" disabled={saving || !preview} className="fc-btn-gold text-xs flex items-center gap-1.5" data-testid="edit-values-submit">
            {saving ? <Loader2 size={12} className="animate-spin" /> : <DollarSign size={12} />} Confirmar
          </button>
        </div>
      </form>
    </ModalShell>
  );
}
